import axios from 'axios';
import { plainToInstance } from 'class-transformer';

import {
  ApiResponse,
  CollectionApiResponse,
  Patient,
  PatientContact,
  User,
} from '../model';
import { AddressResponse, PickupAddressListResponse } from '../model/Address';
import { PatientDocument } from '../model/PatientDocument';
import { TEMP_EMPTY_FOR_PREVIEW } from './temp-empty-preview';

export interface UserDTO {
  address_text?: string;
  birthdate?: string;
  midname?: string;
  name?: string;
  surname?: string;
  avatar?: string;
}

export interface PatientDTO {
  birthdate: string | null;
  code: string;
  gender_id: number;
  midname: string;
  name: string;
  surname: string;
  email: string;
  contacts: PatientContact[];
  documents: PatientDocument[];
  username: string; // phone
}

export class PeopleApi {
  static readonly KEY = 'people';

  static async me(isClient: boolean): Promise<User> {
    const { data } = await axios.get<ApiResponse<User>>(
      isClient ? '/api/v1/client/people' : '/api/v1/people/me',
    );
    return plainToInstance(User, data.data);
  }

  static async getPatients(
    search?: string,
    page?: number,
    size?: number,
  ): Promise<{ total: number; items: Patient[] }> {
    if (TEMP_EMPTY_FOR_PREVIEW) return { total: 0, items: [] };
    const { data } = await axios.get<CollectionApiResponse<Patient>>(
      '/api/v1/people',
      {
        params: {
          search,
          page,
          size,
        },
      },
    );
    return {
      total: data.total,
      items: plainToInstance(Patient, data.items),
    };
  }

  static async getPatientDetails(id: string): Promise<Patient> {
    const { data } = await axios.get<ApiResponse<Patient>>(
      `/api/v1/people/${id}`,
    );

    return plainToInstance(Patient, data.data);
  }

  static async updateMe(body: UserDTO & { avatarFile?: File }): Promise<User> {
    const formData = new FormData();

    if (body.avatarFile) {
      formData.append('avatar', body.avatarFile);
    }
    if (body.name) {
      formData.append('name', body.name);
    }
    if (body.surname) {
      formData.append('surname', body.surname);
    }
    if (body.midname) {
      formData.append('midname', body.midname);
    }
    if (body.birthdate) {
      formData.append('birthdate', body.birthdate);
    }
    if (body.address_text) {
      formData.append('address_text', body.address_text);
    }

    const { data } = await axios.put<ApiResponse<User>>(
      '/api/v1/client/people',
      formData,
    );

    return plainToInstance(User, data.data);
  }

  static async createPatient(body: PatientDTO): Promise<any> {
    const { data } = await axios.post<ApiResponse<any>>(
      '/api/v1/doctor/patient',
      body,
    );

    return data;
  }

  static async sendCodeToPatient(phone: string): Promise<ApiResponse> {
    const { data } = await axios.post<ApiResponse>('/api/v1/doctor/send_code', {
      phone,
    });

    return data;
  }
  static async updatePatient(
    patientId: string,
    updatedData: any,
  ): Promise<ApiResponse> {
    const { data } = await axios.put<ApiResponse>(
      `/api/v1/people/${patientId}`,
      { updatedData },
    );

    return data;
  }
  static async createPatientsAddress(
    addressData: AddressResponse,
  ): Promise<ApiResponse<AddressResponse>> {
    const { data } = await axios.post<ApiResponse<AddressResponse>>(
      `/api/v1/address`,
      addressData,
    );
    return data;
  }

  static async getAddressById(addressId: number): Promise<AddressResponse> {
    const { data } = await axios.get<AddressResponse>(
      `api/v1/address/${addressId}`,
    );

    return data;
  }

  static async getAddress(
    patientId?: number | null,
  ): Promise<{ total: number; items: AddressResponse[] }> {
    const { data } = await axios.get<CollectionApiResponse<AddressResponse>>(
      '/api/v1/address/list',
      {
        params: {
          patientId,
        },
      },
    );
    return {
      total: data.total,
      items: data.items,
    };
  }

  static async getPickupAddress(): Promise<PickupAddressListResponse> {
    const { data } = await axios.get<PickupAddressListResponse>(
      '/api/v1/address/pickup',
    );

    return data;
  }

  static async normalizeAddress(
    search?: string,
  ): Promise<{ total: number; items: AddressResponse[] }> {
    const { data } = await axios.get<CollectionApiResponse<AddressResponse>>(
      '/api/v1/address',
      {
        params: {
          search,
        },
      },
    );
    return {
      total: data.total,
      items: data.items,
    };
  }
}
